import type {ContainerQueryObject, ContainerQueryResult} from '@Hooks/useContainerQuery';
import type {EventType} from '@Components/event-wizard/type-dropdown/type-dropdown.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import type {AudienceEmailState} from '@Components/email-marketing-campaign-audience/email-marketing-campaign-audience.types';
import type {TicketingConfig} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import type {OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {EventTicketingListStats} from '@Components/events/components/event-item/event-item.types';
import type {LoadingStates} from '@Utils/loading.util';
import type {MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {Type} from '@Components/button';
import type React from 'react';

export const NUM_SKELETON_ITEMS = 5;
export const EVENT_ITEM_OPTIONS_PANEL_ID = 'eventItemOptionsPanel';
export type EventHashedID = string;
export type AllEventsMap = Record<EventHashedID, EventVO>;
export type EventStatusFilterMap = Record<EventStatusFilter, boolean>;
export interface EventProps {
  eventsViewType: EventsViewType;
}

export interface EventRegistrationFields {
  areRegistrationsRequired: boolean;
  email: boolean;
  name: boolean;
  phone: boolean;
  registerBtnText: string;
  customBtnTexts?: string[];
  registerBtnLink?: string | null; // `null` on the wire (PHP EventRegistrationFieldVO), `undefined` once the wizard clears it — treat both as "no link"
}

export interface EventRegistrations {
  id: number; // ID from EmailMarketingCampaignMailingListContactVO
  idMailingList: number;
  state: AudienceEmailState;
  displayableBounceReason: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

export interface EventVO {
  hashedID: EventHashedID;
  title: string;
  type: EventType;
  startDateTime: string;
  endDateTime: string;
  showCountdown: boolean;
  isRemoveWatermark: boolean;
  style: EventStyles;
  registrationFields: EventRegistrationFields;
  ticketingConfig?: TicketingConfig;
  organiser?: OrganiserDetails;
  tzcode: string;
  recurring?: RecurringEvent;
  lastModified: string;
  description: string;
  plainTextDescription: string;
  status: EventStatus;
  isDraft: boolean;
  isPublished: boolean;
  img?: string;
  coverPhotoTemplateHID?: string;
  coverPhotoPreviewURL?: string;
  time?: EventTime;
  venue?: PlaceDetails;
  venueOnline?: EventOnlineVenue;
  isShared: boolean;
  isSharedOnGoogle?: boolean;
  isPast: boolean;
  startDateTimestamp: number;
  endDateTimestamp: number;
  numRegistrations: number;
  numViews: number;
  allowRegistrations: boolean;
  creatorId: number;
  timezoneOffsetWithAbbreviation?: string;
  timezoneOffset?: string;
  formattedTimeText?: EventItemFormattedTimeText;
  signupFormID?: string;
  eventAccessInfo?: EventAccessInfo;
  doShowRecurringEventInfo?: boolean;
  ticketingListStats?: EventTicketingListStats;
}

export interface EventAccessInfo {
  sharedByName: string;
  sharedByUserProfilePictureURL: string;
  isSharedInCurrentUserTeam: boolean;
  isExternallyShared: boolean;
  isUserEventTeamAdmin: boolean;
}

export interface EventItemFormattedTimeText {
  titleRowMainText: string;
  titleRowExtraText: string;
  dateTextBeforeSubtitleText: string;
  subtitleTimeText: string;
}

export interface EventVOMinifiedWizardPrefill {
  startDateTime?: string;
  endDateTime?: string;
  designTemplateHID?: string;
  designTemplateURL?: string;
  designTemplateSource?: MediaTypes;
  startTime?: string;
  endTime?: string;
  startDate?: string;
  endDate?: string;
  eventName?: string;
  eventType?: EventType;
  location?: string;
  /** The hashed filename of the event preset image to use as the cover. */
  eventPresetImageHash: string | null;
  coverPreviewURL: string | null;
  backgroundColor: string | null;
  fontFamily: string | null;
  isContentPrefillSkipped?: boolean;
}

export interface EventOnlineVenue {
  link: string;
}

export interface EventTime {
  date_start: string;
  date_end: string;
  timezone: string;
}

/**
 * The Status checkboxes in My Stuff. OR'd together, and each names a disjoint set of events: LIVE, UPCOMING
 * and PAST are published events in that time bucket, DRAFT is every draft whatever its dates. A draft is
 * reachable through DRAFT and nothing else, so leaving it false is how a caller asks for published only.
 *
 * Ticking all four, or none, applies no filter at all -- fine for a person, a trap for code. So `DRAFT: false`
 * excludes nothing by itself: only a ticked box emits a clause, so a caller that wants published events only
 * must also tick at least one of LIVE / UPCOMING / PAST. See the PHP `EventStatusFilter` and
 * `EventMyStuffFilters::applyStatusFilters`; the integers are a shared wire format.
 */
export enum EventStatusFilter {
  LIVE = 1,
  UPCOMING = 2,
  PAST = 3,
  DRAFT = 4,
}

export enum EventStatus {
  DRAFT = 2,
  PUBLISHED = 1,
}

export enum ScreenSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  XLARGE = 'xlarge',
}

export enum EventsViewType {
  DEFAULT = 'default',
  SHARED_WITH_ME = 'shared_with_me',
}

export enum EventSortBy {
  LAST_MODIFIED = 1,
  EVENT_START_DATE = 2,
  ALPHABETICAL_ORDER_ASC = 3,
  ALPHABETICAL_ORDER_DESC = 4,
}

export enum UserEventCreationPermissionState {
  NEEDS_UPSELL = 'needs-upsell',
  VALIDATING_PERMISSIONS = 'validating-permissions',
  CAN_CREATE = 'can-create-events',
}

export enum EventFiltersVariant {
  DESKTOP = 'desktop',
  ICON_ONLY = 'icon-only',
  COMPACT = 'compact',
}

export enum EventsSharedWithMeFilter {
  ALL = 'all',
  COPROMOTERS = 'copromoters',
  TEAM = 'team',
}

/** What the events container query reports at the mobile shell before its first measurement. */
export const UNMEASURED_MOBILE_CONTAINER_QUERY_RESULT: ContainerQueryResult = {
  small: true,
  emptyStateSmall: true,
};

export const isContainerMeasured = (containerQueryResult: ContainerQueryResult): boolean => {
  return Object.keys(containerQueryResult).length > 0;
};

export const CONTAINER_QUERY: ContainerQueryObject = {
  small: {
    maxWidth: 456,
  },

  medium: {
    minWidth: 456,
    maxWidth: 650,
  },

  large: {
    minWidth: 650,
    maxWidth: 1020,
  },

  xlarge: {
    minWidth: 1020,
  },

  emptyStateSmall: {
    minWidth: 0,
    maxWidth: 550,
  },

  emptyStateMedium: {
    minWidth: 551,
    maxWidth: 767,
  },

  emptyStateLarge: {
    minWidth: 768,
    maxWidth: 950,
  },

  calendarSectionLarge: {
    minWidth: 850,
  },
};

export interface EventsFilters {
  statusFilter: EventStatusFilterMap;
  searchTerm: string;
  sortType: EventSortBy;
  sharedEventsActiveFilter: EventsSharedWithMeFilter;
}

export interface EventsListLazyData {
  ids: EventHashedID[];
  loadingState: LoadingStates;
  loadMore: boolean;
}

export interface EventsReduxState {
  eventsViewType: EventsViewType;
  eventsListDefaultLazyData: EventsListLazyData;
  eventsListFilteredLazyData: EventsListLazyData;
  allEvents: AllEventsMap;
  isListViewSelected: boolean;
  filters: EventsFilters;
  userEventCreationPermissionState: UserEventCreationPermissionState;
  doShowEmbedBanner: boolean;
  // The Stripe (payments) tab is shown only to organisers who have set up ticketing before; hidden for users who
  // have never run a ticketed event, so it doesn't clutter the events nav for them.
  doShowStripeTab: boolean;
  pageMetaDataLoadingState: LoadingStates;
  calendarSectionLoadingState: LoadingStates;
  eventCalendarMetaData?: EventCalendarMetaData;
}

export interface FetchEventsFromServerParams {
  eventsViewType: EventsViewType;
  statusFilters: EventStatusFilterMap;
  searchTerm: string;
  sort: EventSortBy;
  offset: number;
  sharedEventFilter: EventsSharedWithMeFilter;
}

export interface EventsPageMetaDataResponse {
  doShowUpsell: boolean;
  doShowEmbedBanner: boolean;
  doShowStripeTab: boolean;
}

export interface EventCalendarSectionResponse {
  eventCalendarMetaData?: EventCalendarMetaData;
}

export interface CreateEventButtonCommonProps {
  buttonType: Type;
  onClick: React.MouseEventHandler;
  isLoading: boolean;
  showPremiumUpsellingIcon: boolean;
}

export interface EventCalendarMetaData {
  calendarHID: string;
  calendarName: string;
  numLiveFutureEvents: number;
  numUpcomingEventsThisWeek: number;
  numSocialConnectedAccounts: number;
  singleSocialPlatformName: string | null;
  numMailingLists: number;
  hasSenderProfile: boolean;
  topXEventImages: string[];
}

export enum EventActionCompletedCustomEvent {
  EVENT_UPDATED = 'event-updated',
}
